"use client";

import { PointerInteractionAutoProvider } from "@/components/feature/pointer-interaction-media";
import { PreviewIntentAutoProvider } from "@/components/feature/preview-intent";
import { ShowcaseTile } from "@/components/feature/showcase-tile";
import type { PageOriginContext } from "@/lib/page-origin-context";
import type { ShowcaseGridDisplayItem } from "@/types/showcase-grid";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import React, { useRef, useState, useEffect } from "react";
import type { TouchEvent } from "react";

interface GameRowProps {
  tag: {
    id: number;
    name: string;
    slug?: string;
    description: string | null;
    display_order: number | null;
  };
  /**
   * Every tile in the row, games and assets alike. This is the only scrolling row, so a
   * section's content never changes how it scrolls.
   */
  items: ShowcaseGridDisplayItem[];
  /** Uniquely identifies this row on the page for hover preview (shared PreviewIntentStore). */
  previewRowScope: string;
  visibleGameCount?: number; // Default to 4
  pageOrigin?: PageOriginContext | null;
  /** When false, the section title is omitted. */
  showHeader?: boolean;
}

export function GameRow({
  tag,
  items,
  previewRowScope,
  visibleGameCount = 4,
  pageOrigin,
  showHeader = true,
}: GameRowProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const rowRef = useRef<HTMLDivElement>(null);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const [canHover, setCanHover] = useState(false);
  const [isThinDesktop, setIsThinDesktop] = useState(false);
  const [edgeLeftPx, setEdgeLeftPx] = useState(0);
  const [edgeRightPx, setEdgeRightPx] = useState(0);
  const baseLen = items.length;
  const shouldLoopRight = baseLen >= 5;
  const [repeatCount, setRepeatCount] = useState(shouldLoopRight ? 3 : 1);
  const touchStartX = useRef<number | null>(null);

  // Responsive measurements
  const [measuredItemWidth, setMeasuredItemWidth] = useState(224 + 16); // Default fallback (lg:w-56 + gap-4)
  const [actualVisibleCount, setActualVisibleCount] = useState(visibleGameCount);

  // Measure actual item dimensions and calculate visible count
  useEffect(() => {
    if (typeof window === "undefined") return;

    const getFirstItem = (): HTMLElement | null => {
      if (!containerRef.current) return null;
      return containerRef.current.querySelector('[data-testid="game-row-item"]') as HTMLElement;
    };

    const getContainerRects = () => {
      return {
        itemRect: getFirstItem()?.getBoundingClientRect() || null,
        rowRect: rowRef.current?.getBoundingClientRect() || null,
      } as { itemRect: DOMRect | null; rowRect: DOMRect | null };
    };

    const computeItemWidthWithGap = (itemRect: DOMRect | null): number | null => {
      if (!containerRef.current || !itemRect) return null;
      const containerStyle = window.getComputedStyle(containerRef.current);
      const gap = Number.parseInt(containerStyle.gap) || 16;
      return itemRect.width + gap;
    };

    const computeVisibleCount = (containerWidth: number, itemWidthWithGap: number) => {
      const calculated = Math.floor(containerWidth / itemWidthWithGap);
      return calculated > 0
        ? Math.max(1, Math.min(calculated, visibleGameCount * 2))
        : visibleGameCount;
    };

    const setVisibleCountFromRects = (rowRect: DOMRect | null, itemWidthWithGap: number) => {
      const containerWidth = rowRect?.width || 0;
      if (containerWidth > 0) {
        const clampedVisibleCount = computeVisibleCount(containerWidth, itemWidthWithGap);
        setActualVisibleCount(clampedVisibleCount);
      } else {
        setActualVisibleCount(visibleGameCount);
      }
    };

    const measureItems = () => {
      if (!containerRef.current || !rowRef.current) {
        // Fallback to passed visibleGameCount if measurement fails
        setActualVisibleCount(visibleGameCount);
        return;
      }

      const firstItem = getFirstItem();
      if (!firstItem) {
        // Fallback to passed visibleGameCount if no items found
        setActualVisibleCount(visibleGameCount);
        return;
      }

      const { itemRect, rowRect } = getContainerRects();
      const itemWidthWithGap = computeItemWidthWithGap(itemRect);

      if (itemWidthWithGap && itemWidthWithGap > 0) {
        setMeasuredItemWidth(itemWidthWithGap);
        setVisibleCountFromRects(rowRect, itemWidthWithGap);
      } else {
        setActualVisibleCount(visibleGameCount);
      }
    };

    // Debounce measurements to avoid excessive recalculation
    let timeoutId: NodeJS.Timeout;
    const debouncedMeasure = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(measureItems, 100);
    };

    // Initial measurement with a slight delay to ensure DOM is ready
    const initialTimeout = setTimeout(measureItems, 50);

    // Re-measure on resize
    window.addEventListener("resize", debouncedMeasure);

    return () => {
      window.removeEventListener("resize", debouncedMeasure);
      clearTimeout(timeoutId);
      clearTimeout(initialTimeout);
    };
  }, [visibleGameCount, items.length]); // Re-run when tiles change

  // Detect if device can hover (not touch-only mobile)
  useEffect(() => {
    if (typeof window === "undefined") return;

    const hoverQuery = window.matchMedia("(hover: hover)");
    setCanHover(hoverQuery.matches);

    const handleChange = (e: MediaQueryListEvent | MediaQueryList) => {
      setCanHover(e.matches);
    };

    // Chrome 87 and similar legacy engines only support addListener/removeListener.
    if (typeof hoverQuery.addEventListener === "function") {
      hoverQuery.addEventListener("change", handleChange as EventListener);
      return () => hoverQuery.removeEventListener("change", handleChange as EventListener);
    }

    hoverQuery.addListener(handleChange);
    return () => hoverQuery.removeListener(handleChange);
  }, []);

  // Compute offsets so arrows can align with the screen edges on thin desktop (hover-capable)
  useEffect(() => {
    if (typeof window === "undefined") return;
    const computeOffsets = () => {
      if (!rowRef.current) return;
      const rect = rowRef.current.getBoundingClientRect();
      const vw = window.innerWidth || document.documentElement.clientWidth || 0;
      const thin = canHover && vw < 1024; // below Tailwind lg breakpoint
      setIsThinDesktop(thin);
      if (thin) {
        setEdgeLeftPx(Math.max(0, Math.round(rect.left)));
        setEdgeRightPx(Math.max(0, Math.round(vw - rect.right)));
      } else {
        setEdgeLeftPx(0);
        setEdgeRightPx(0);
      }
    };
    computeOffsets();
    window.addEventListener("resize", computeOffsets);
    return () => window.removeEventListener("resize", computeOffsets);
  }, [canHover]);

  // Reset scroll position on mount and when crossing the smallest breakpoint (<640px)
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 639px)");
    const handleChange = () => {
      // Reset to the beginning to avoid misalignment on layout changes
      setScrollPosition(0);
      setDragOffset(0);
    };
    // Initialize and subscribe
    handleChange();
    mq.addEventListener("change", handleChange);
    return () => mq.removeEventListener("change", handleChange);
  }, []);

  // A row that changes length starts over, so a row that no longer loops drops its copies.
  useEffect(() => {
    setRepeatCount(shouldLoopRight ? 3 : 1);
    setScrollPosition(0);
    setDragOffset(0);
  }, [baseLen, shouldLoopRight]);

  // Calculate how many games we can scroll through
  const copyCount = shouldLoopRight ? repeatCount : 1;
  const extendedLen = copyCount * baseLen;
  const maxScrollPosition = Math.max(
    0,
    (shouldLoopRight ? extendedLen : baseLen) - actualVisibleCount
  );
  const canScrollLeft = scrollPosition > 0;
  const canScrollRight = shouldLoopRight ? true : scrollPosition < maxScrollPosition;

  const handlePrev = () => {
    if (!canScrollLeft) return;
    const newPosition = Math.max(0, scrollPosition - 2);
    setScrollPosition(newPosition);
  };

  const handleNext = () => {
    if (!canScrollRight) return;
    const newPosition = Math.min(maxScrollPosition, scrollPosition + 2);
    setScrollPosition(newPosition);
  };

  // Touch handlers for mobile drag
  const handleTouchStart = (e: TouchEvent<HTMLDivElement>) => {
    const touch = e.touches[0];
    if (!touch) return;

    touchStartX.current = touch.clientX;
    setIsDragging(true);
    setDragOffset(0);
  };

  const handleTouchMove = (e: TouchEvent<HTMLDivElement>) => {
    if (!isDragging || touchStartX.current === null) return;

    const touch = e.touches[0];
    if (!touch) return;

    const deltaX = touch.clientX - touchStartX.current;

    // Only update drag offset if we're actually dragging horizontally
    if (Math.abs(deltaX) > 10) {
      setDragOffset(deltaX);
    }
  };

  const handleTouchEnd = () => {
    if (!isDragging || touchStartX.current === null) {
      setIsDragging(false);
      setDragOffset(0);
      touchStartX.current = null;
      return;
    }

    // Determine if we should snap to next/prev position
    const threshold = 50; // Minimum drag distance to trigger scroll

    if (Math.abs(dragOffset) > threshold) {
      if (dragOffset > 0 && canScrollLeft) {
        // Dragged right, go to previous
        handlePrev();
      } else if (dragOffset < 0 && canScrollRight) {
        // Dragged left, go to next
        handleNext();
      }
    }

    // Reset drag state
    setIsDragging(false);
    setDragOffset(0);
    touchStartX.current = null;
  };

  // Grow the extended list to the right if nearing the end
  useEffect(() => {
    if (!shouldLoopRight) return;
    const remainingRight = extendedLen - (scrollPosition + actualVisibleCount);
    if (remainingRight < baseLen) {
      setRepeatCount((c) => c + 1);
    }
  }, [scrollPosition, actualVisibleCount, extendedLen, baseLen, shouldLoopRight]);

  return (
    <div ref={rowRef} className="relative group/row" data-testid="game-row-root">
      {/* Header row with title */}
      {showHeader ? (
        <div className="flex items-end justify-between px-1">
          <h3 className="text-white text-xl font-bold">{tag.name}</h3>
        </div>
      ) : null}

      {/* Games container */}
      {/*
        md:overflow-hidden clips tiles that slide past the row. The padding is
        inside that clip so the NEW badge, which sits just outside each tile,
        stays visible.
      */}
      <div
        data-testid="game-row-scroller"
        className="w-full max-w-full touch-pan-y overflow-visible px-3 pt-4 md:overflow-hidden"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
      >
        <PreviewIntentAutoProvider>
          <PointerInteractionAutoProvider>
            <div
              ref={containerRef}
              className={`flex gap-4 ${isDragging ? "" : "transition-transform duration-500 ease-in-out"}`}
              style={{
                transform: `translateX(-${scrollPosition * measuredItemWidth - dragOffset}px)`, // Measured item width + gap, with drag offset
              }}
            >
              {Array.from({ length: copyCount }).map((_, copyIdx) => (
                <React.Fragment key={copyIdx}>
                  {items.map((item) => (
                    <div
                      key={`${copyIdx}-${item.id}`}
                      className="w-[26vw] sm:w-44 md:w-52 lg:w-56 h-[26vw] sm:h-44 md:h-52 lg:h-56 flex-shrink-0"
                      data-testid="game-row-item"
                      data-tile-type={item.type}
                    >
                      <ShowcaseTile
                        item={item}
                        previewId={`${previewRowScope}:${item.id}:${copyIdx}`}
                        pageOrigin={pageOrigin}
                      />
                    </div>
                  ))}
                </React.Fragment>
              ))}
            </div>
          </PointerInteractionAutoProvider>
        </PreviewIntentAutoProvider>
      </div>

      {/* Left Arrow - Only show on hover-capable devices (not touch-only mobile) */}
      {canScrollLeft && canHover && (
        <button
          onClick={handlePrev}
          className="flex absolute top-1/2 -translate-y-1/2 z-20 h-20 w-10 md:h-24 md:w-12 rounded-r-full bg-black/60 hover:bg-black/80 text-white items-center justify-center opacity-0 group-hover/row:opacity-100 transition-opacity pointer-events-none group-hover/row:pointer-events-auto"
          style={{ left: isThinDesktop ? `calc(${edgeLeftPx * -1}px)` : 0 }}
          aria-label="Previous games"
          data-testid="game-row-left-arrow"
        >
          <CaretLeft weight="bold" className="w-6 h-6" />
        </button>
      )}

      {/* Right Arrow - Only show on hover-capable devices (not touch-only mobile) */}
      {canScrollRight && canHover && (
        <button
          onClick={handleNext}
          className="flex absolute top-1/2 -translate-y-1/2 z-20 h-20 w-10 md:h-24 md:w-12 rounded-l-full bg-black/60 hover:bg-black/80 text-white items-center justify-center opacity-0 group-hover/row:opacity-100 transition-opacity pointer-events-none group-hover/row:pointer-events-auto"
          style={{ right: isThinDesktop ? `calc(${edgeRightPx * -1}px)` : 0 }}
          aria-label="Next games"
          data-testid="game-row-right-arrow"
        >
          <CaretRight weight="bold" className="w-6 h-6" />
        </button>
      )}
    </div>
  );
}
